import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Home, Globe, Phone, Mail, MessageSquare, RefreshCw, Search,
  Archive, Trash2, CheckCircle, Clock, AlertCircle, ChevronDown,
  ChevronUp, X, User, Smartphone, Calendar, FileText, PhoneCall,
  ArchiveRestore, Filter, ExternalLink
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export default function WebsiteQuotesPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [quotes, setQuotes] = useState([]);
  const [filteredQuotes, setFilteredQuotes] = useState([]);
  const [stats, setStats] = useState({ total: 0, nouveau: 0, en_cours: 0, archived: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showArchived, setShowArchived] = useState(false);
  const [expandedQuote, setExpandedQuote] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchQuotes();
    fetchStats();
  }, []);

  useEffect(() => {
    filterQuotes();
  }, [quotes, searchTerm, statusFilter, showArchived]);

  const fetchQuotes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/website-quotes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuotes(response.data);
    } catch (error) {
      console.error('Error fetching quotes:', error);
      toast.error('Erreur lors du chargement des devis');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/api/website-quotes/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const filterQuotes = () => {
    let filtered = quotes;

    // Filter by archived status
    filtered = filtered.filter(q => showArchived ? q.archived : !q.archived);

    // Filter by search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(q =>
        q.name?.toLowerCase().includes(search) ||
        q.phone?.includes(search) ||
        q.email?.toLowerCase().includes(search) ||
        q.model?.toLowerCase().includes(search) ||
        q.problem?.toLowerCase().includes(search)
      );
    }

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(q => q.status === statusFilter);
    }

    setFilteredQuotes(filtered);
  };

  const handleMarkCalled = async (quoteId) => {
    setActionLoading(quoteId);
    try {
      await axios.post(`${API}/api/website-quotes/${quoteId}/mark-called`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Marqué comme rappelé');
      fetchQuotes();
      fetchStats();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setActionLoading(null);
    }
  };

  const handleMarkCompleted = async (quoteId) => {
    setActionLoading(quoteId);
    try {
      await axios.post(`${API}/api/website-quotes/${quoteId}/mark-completed`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Marqué comme terminé');
      fetchQuotes();
      fetchStats();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setActionLoading(null);
    }
  };

  const handleArchive = async (quoteId, archive) => {
    setActionLoading(quoteId);
    try {
      await axios.patch(`${API}/api/website-quotes/${quoteId}/archive`, 
        { archived: archive },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(archive ? 'Archivé' : 'Désarchivé');
      fetchQuotes();
      fetchStats();
    } catch (error) {
      toast.error('Erreur lors de l\'archivage');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (quoteId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette demande ?')) return;
    
    setActionLoading(quoteId);
    try {
      await axios.delete(`${API}/api/website-quotes/${quoteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Supprimé');
      fetchQuotes();
      fetchStats();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'nouveau': { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30', icon: AlertCircle, label: 'Nouveau' },
      'en_cours': { bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30', icon: Clock, label: 'En cours' },
      'termine': { bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/30', icon: CheckCircle, label: 'Terminé' },
      'annule': { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30', icon: X, label: 'Annulé' }
    };
    const badge = badges[status] || badges['nouveau'];
    const Icon = badge.icon;
    return (
      <span className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full ${badge.bg} ${badge.text} border ${badge.border}`}>
        <Icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };

  const formatPhoneForCall = (phone) => {
    if (!phone) return null;
    // Remove spaces and format for tel: link
    return phone.replace(/\s/g, '');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900">
      {/* Header */}
      <header className="bg-gray-800/50 backdrop-blur-sm border-b border-gray-700 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-3 md:px-4 py-2 md:py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-4">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              size="sm"
              className="text-white/70 hover:text-white h-8 px-2"
              data-testid="home-nav-button"
            >
              <Home className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Accueil</span>
            </Button>
            <div className="flex items-center gap-2">
              <Globe className="w-5 h-5 md:w-6 md:h-6 text-cyan-400" />
              <h1 className="text-base md:text-xl font-bold text-white">Demandes & Devis Site Web</h1>
            </div>
          </div>

          <a
            href="https://phonelab69.fr/admin/devis"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 md:gap-2 text-cyan-400 hover:text-cyan-300 text-xs md:text-sm"
          >
            <ExternalLink className="w-4 h-4" />
            <span className="hidden sm:inline">Ouvrir phonelab69.fr</span>
          </a>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 md:px-4 py-4 md:py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 mb-4 md:mb-6">
          <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-3 md:p-4">
            <div className="text-xl md:text-3xl font-bold text-white">{stats.total}</div>
            <div className="text-xs md:text-sm text-gray-400">Total demandes</div>
          </div>
          <div 
            onClick={() => { setStatusFilter('nouveau'); setShowArchived(false); }}
            className="bg-blue-500/10 rounded-xl border border-blue-500/30 p-3 md:p-4 cursor-pointer hover:bg-blue-500/20 transition-colors"
          >
            <div className="text-xl md:text-3xl font-bold text-blue-400">{stats.nouveau}</div>
            <div className="text-xs md:text-sm text-blue-300">Nouveaux</div>
          </div>
          <div 
            onClick={() => { setStatusFilter('en_cours'); setShowArchived(false); }}
            className="bg-amber-500/10 rounded-xl border border-amber-500/30 p-3 md:p-4 cursor-pointer hover:bg-amber-500/20 transition-colors"
          >
            <div className="text-xl md:text-3xl font-bold text-amber-400">{stats.en_cours}</div>
            <div className="text-xs md:text-sm text-amber-300">En cours</div>
          </div>
          <div 
            onClick={() => setShowArchived(true)}
            className="bg-gray-700/50 rounded-xl border border-gray-600 p-3 md:p-4 cursor-pointer hover:bg-gray-700 transition-colors"
          >
            <div className="text-xl md:text-3xl font-bold text-gray-300">{stats.archived}</div>
            <div className="text-xs md:text-sm text-gray-400">Archivés</div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-3 md:p-4 mb-4 md:mb-6">
          <div className="flex flex-col md:flex-row gap-3 md:gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Rechercher..."
                  className="pl-10 bg-white/10 border-white/20 text-white text-sm"
                  data-testid="search-input"
                />
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="bg-white/10 border border-white/20 rounded-lg px-2 md:px-3 py-2 text-white text-xs md:text-sm flex-1 md:flex-none"
                data-testid="status-filter"
              >
                <option value="all">Tous</option>
                <option value="nouveau">Nouveau</option>
                <option value="en_cours">En cours</option>
                <option value="termine">Terminé</option>
              </select>

            <Button
              onClick={() => setShowArchived(!showArchived)}
              variant={showArchived ? "default" : "outline"}
              size="sm"
              className={`text-xs ${showArchived ? "bg-gray-600" : "border-white/20 text-white hover:bg-white/10"}`}
              data-testid="toggle-archived"
            >
              <Archive className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">{showArchived ? "Voir actifs" : "Voir archivés"}</span>
            </Button>

            <Button
              onClick={() => { fetchQuotes(); fetchStats(); }}
              variant="outline"
              size="sm"
              className="border-white/20 text-white hover:bg-white/10 text-xs"
              data-testid="refresh-button"
            >
              <RefreshCw className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Actualiser</span>
            </Button>
            </div>
          </div>

          <div className="mt-2 md:mt-3 text-xs md:text-sm text-gray-400">
            {filteredQuotes.length} demande{filteredQuotes.length > 1 ? 's' : ''} {showArchived ? 'archivée(s)' : ''}
          </div>
        </div>

        {/* Quotes List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <RefreshCw className="w-8 h-8 text-cyan-400 animate-spin" />
          </div>
        ) : filteredQuotes.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <Globe className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Aucune demande {showArchived ? 'archivée' : ''} pour le moment</p>
            <p className="text-sm mt-2">Les demandes de devis de phonelab69.fr apparaîtront ici</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredQuotes.map((quote) => (
              <div
                key={quote.id}
                className={`bg-gray-800/50 rounded-xl border transition-all ${
                  expandedQuote === quote.id ? 'border-cyan-500/50' : 'border-gray-700 hover:border-gray-600'
                }`}
                data-testid={`quote-card-${quote.id}`}
              >
                {/* Quote Header */}
                <div
                  className="p-4 cursor-pointer"
                  onClick={() => setExpandedQuote(expandedQuote === quote.id ? null : quote.id)}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4 flex-1">
                      {/* Avatar */}
                      <div className="w-12 h-12 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                        <User className="w-6 h-6 text-cyan-400" />
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-white">{quote.name}</h3>
                          {getStatusBadge(quote.status)}
                          {quote.isNew && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-red-500 text-white animate-pulse">
                              NOUVEAU
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-400">
                          {quote.device_info && (
                            <span className="flex items-center gap-1">
                              <Smartphone className="w-3 h-3" />
                              {quote.device_info}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {quote.formatted_date}
                          </span>
                        </div>

                        <div className="mt-2 text-sm text-gray-300 line-clamp-2">
                          {quote.problem || quote.message || 'Pas de description'}
                        </div>
                      </div>
                    </div>

                    {/* Quick Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {quote.phone && (
                        <a
                          href={`tel:${formatPhoneForCall(quote.phone)}`}
                          onClick={(e) => e.stopPropagation()}
                          className="p-2 rounded-lg bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 transition-colors"
                          title="Appeler"
                        >
                          <Phone className="w-5 h-5" />
                        </a>
                      )}
                      {expandedQuote === quote.id ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Content */}
                {expandedQuote === quote.id && (
                  <div className="border-t border-gray-700 p-4 space-y-4">
                    {/* Contact Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {quote.phone && (
                        <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-700/50">
                          <Phone className="w-5 h-5 text-emerald-400" />
                          <div>
                            <div className="text-xs text-gray-400">Téléphone</div>
                            <a 
                              href={`tel:${formatPhoneForCall(quote.phone)}`}
                              className="text-white hover:text-emerald-400"
                            >
                              {quote.phone}
                            </a>
                          </div>
                        </div>
                      )}
                      {quote.email && (
                        <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-700/50">
                          <Mail className="w-5 h-5 text-blue-400" />
                          <div>
                            <div className="text-xs text-gray-400">Email</div>
                            <a 
                              href={`mailto:${quote.email}`}
                              className="text-white hover:text-blue-400 truncate block"
                            >
                              {quote.email}
                            </a>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Device Info */}
                    {(quote.brand || quote.model || quote.deviceType) && (
                      <div className="p-3 rounded-lg bg-gray-700/50">
                        <div className="text-xs text-gray-400 mb-1">Appareil</div>
                        <div className="flex items-center gap-2 text-white">
                          <Smartphone className="w-4 h-4 text-cyan-400" />
                          {quote.brand && <span className="capitalize">{quote.brand}</span>}
                          {quote.model && <span>{quote.model}</span>}
                          {quote.deviceType && <span className="text-gray-400">({quote.deviceType})</span>}
                        </div>
                      </div>
                    )}

                    {/* Problem Description */}
                    {(quote.problem || quote.message) && (
                      <div className="p-3 rounded-lg bg-gray-700/50">
                        <div className="text-xs text-gray-400 mb-1">Description du problème</div>
                        <p className="text-white whitespace-pre-wrap">{quote.problem}</p>
                        {quote.message && quote.message !== quote.problem && (
                          <p className="text-gray-300 mt-2 whitespace-pre-wrap">{quote.message}</p>
                        )}
                      </div>
                    )}

                    {/* Promo Code */}
                    {quote.promo_code && (
                      <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/30">
                        <div className="text-xs text-purple-300 mb-1">Code Promo</div>
                        <div className="text-purple-400 font-mono">{quote.promo_code}</div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-700">
                      {!quote.archived && quote.status === 'nouveau' && (
                        <Button
                          onClick={() => handleMarkCalled(quote.id)}
                          disabled={actionLoading === quote.id}
                          className="bg-emerald-500 hover:bg-emerald-600 text-white"
                          data-testid={`mark-called-${quote.id}`}
                        >
                          {actionLoading === quote.id ? (
                            <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <PhoneCall className="w-4 h-4 mr-2" />
                          )}
                          Marquer rappelé
                        </Button>
                      )}

                      {!quote.archived && quote.status === 'en_cours' && (
                        <Button
                          onClick={() => handleMarkCompleted(quote.id)}
                          disabled={actionLoading === quote.id}
                          className="bg-blue-500 hover:bg-blue-600 text-white"
                          data-testid={`mark-completed-${quote.id}`}
                        >
                          {actionLoading === quote.id ? (
                            <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <CheckCircle className="w-4 h-4 mr-2" />
                          )}
                          Marquer terminé
                        </Button>
                      )}

                      {quote.phone && (
                        <a
                          href={`sms:${formatPhoneForCall(quote.phone)}`}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-green-500/20 text-green-400 hover:bg-green-500/30 transition-colors"
                        >
                          <MessageSquare className="w-4 h-4" />
                          SMS
                        </a>
                      )}

                      <Button
                        onClick={() => handleArchive(quote.id, !quote.archived)}
                        disabled={actionLoading === quote.id}
                        variant="outline"
                        className="border-gray-600 text-gray-300 hover:bg-gray-700"
                        data-testid={`archive-${quote.id}`}
                      >
                        {quote.archived ? (
                          <>
                            <ArchiveRestore className="w-4 h-4 mr-2" />
                            Désarchiver
                          </>
                        ) : (
                          <>
                            <Archive className="w-4 h-4 mr-2" />
                            Archiver
                          </>
                        )}
                      </Button>

                      <Button
                        onClick={() => handleDelete(quote.id)}
                        disabled={actionLoading === quote.id}
                        variant="outline"
                        className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                        data-testid={`delete-${quote.id}`}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Supprimer
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
